
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import BookingsList from '@/components/admin/BookingsList';
import ProjectsList from '@/components/admin/ProjectsList';
import NotificationCenter from '@/components/admin/NotificationCenter';
import { LeadsList } from '@/components/admin/LeadsList';
import { 
  BookingData,
  ProjectOrder, 
  useAdminStore 
} from '@/hooks/use-admin-store';
import Navbar from '@/components/Navbar';
import { toast } from '@/hooks/use-toast';
import { Search, Bell, ArrowDown, LogOut } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/hooks/use-auth';
import { WordPressDownloadsList } from '@/components/admin/WordPressDownloadsList';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { signOut } = useAuth();

  const { 
    bookings,
    projects, 
    notifications,
    leads,
    wordpressDownloads,
    fetchBookings, 
    fetchProjects, 
    fetchLeads,
    fetchWordPressDownloads,
    updateBookingStatus, 
    updateProjectStatus,
    markNotificationsAsRead
  } = useAdminStore();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchBookings(),
          fetchProjects(),
          fetchLeads(),
          fetchWordPressDownloads()
        ]);
      } catch (error) {
        console.error('Error loading admin data:', error);
        toast({
          title: "Error",
          description: "Failed to load data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    // Subscribe to real-time updates for new bookings and project orders
    const bookingsChannel = supabase
      .channel('public:consultations')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'consultations' 
      }, loadData)
      .subscribe();

    const projectsChannel = supabase
      .channel('public:project_orders')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'project_orders' 
      }, loadData)
      .subscribe();

    // Subscribe to real-time updates for leads
    const leadsChannel = supabase
      .channel('public:leads')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'leads' 
      }, loadData)
      .subscribe();

    // Subscribe to real-time updates for WordPress downloads
    const wpDownloadsChannel = supabase
      .channel('public:wordpress_downloads')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'wordpress_downloads' 
      }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(projectsChannel);
      supabase.removeChannel(leadsChannel);
      supabase.removeChannel(wpDownloadsChannel);
    };
  }, [fetchBookings, fetchProjects, fetchLeads, fetchWordPressDownloads]);

  useEffect(() => {
    // Count unread notifications
    const count = notifications.filter(notif => !notif.read).length;
    setUnreadCount(count);
  }, [notifications]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.name.toLowerCase().includes(search.toLowerCase()) ||
      booking.email.toLowerCase().includes(search.toLowerCase()) ||
      booking.businessType.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.customerName.toLowerCase().includes(search.toLowerCase()) ||
      project.customerEmail.toLowerCase().includes(search.toLowerCase()) ||
      project.serviceType.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || project.paymentStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      markNotificationsAsRead();
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex justify-between items-center px-4 pt-8">
        <PageHeader 
          title={<>Admin <span className="text-gold">Dashboard</span></>}
          subtitle="Manage bookings, projects, and customer relationships"
        />
        <Button 
          variant="outline" 
          onClick={signOut}
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>
      </div>
      
      <main className="flex-grow px-4 py-8">
        <div className="container mx-auto max-w-7xl">
          {/* Top Controls */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email..."
                value={search}
                onChange={handleSearch}
                className="pl-9 w-full sm:w-[300px]"
              />
            </div>
            
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={toggleNotifications}
                  className="relative"
                >
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
                
                {showNotifications && (
                  <NotificationCenter 
                    notifications={notifications} 
                    onClose={() => setShowNotifications(false)}
                  />
                )}
              </div>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
            </div>
          ) : (
            <Tabs defaultValue="bookings" className="w-full">
              <TabsList className="grid grid-cols-4 w-full max-w-2xl mx-auto mb-6">
                <TabsTrigger value="bookings">Consultations ({bookings.length})</TabsTrigger>
                <TabsTrigger value="projects">Projects ({projects.length})</TabsTrigger>
                <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
                <TabsTrigger value="wordpress">WP Downloads ({wordpressDownloads.length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="bookings">
                <Card>
                  <CardContent className="pt-6">
                    <BookingsList 
                      bookings={filteredBookings} 
                      onUpdateStatus={updateBookingStatus}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="projects">
                <Card>
                  <CardContent className="pt-6">
                    <ProjectsList 
                      projects={filteredProjects} 
                      onUpdateStatus={updateProjectStatus}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="leads">
                <LeadsList 
                  leads={leads} 
                  loading={isLoading}
                />
              </TabsContent>

              <TabsContent value="wordpress">
                <WordPressDownloadsList 
                  downloads={wordpressDownloads} 
                  loading={isLoading}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
